/**
 * FoldersGuide: the visual guide of the Folders page (/folders, /folders/:uuid), opened by the "?" after the page's
 * title (HelpTitle helpId="folders"). The page draws the folders as a Finder window: the folders as folder icons with
 * their question counts, a folder's subjects inside it (its path above its name), a subject opening its questions in
 * the bank; a search narrows the level shown; each folder's actions sit in its "⋯".
 * It follows the reader's role the way the page builds the menus (Folders.js): a subject's "⋯" holds the questions
 * and the matrix for everyone, the subject variants and Share for its moderator and admins, a test from its approved
 * questions for whoever makes tests, settings and delete for admins; only admins create folders and add subjects.
 * Phones: the "⋯" is always on a tile under 768px (from md up it shows on hover or focus), framed.
 * Props, from the page: `makesTests` (the reader opens the test bank: the menu's "Make a test" and its step),
 * `noFolder` (questions outside every folder exist: the last "No folder" tile of the top level).
 */
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckBadgeIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  FolderIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  ShareIcon,
  ShieldCheckIcon,
  TableCellsIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { HelpFlow, HelpIntro, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  MiniButton,
  MiniCard,
  MiniDialog,
  MiniDivider,
  MiniField,
  MiniIcon,
  MiniMenu,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniText,
} from "../Mini";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";
import { UserContext } from "../../../utils/contexts/UserContext";
import { canBuildVariants, canShareFolder, isAdminUser } from "../../../utils/folders";

export default function FoldersGuide({ makesTests = false, noFolder = false }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  // The page's breakpoint: the tiles' "⋯" shows on hover from md up, always (framed) on phones.
  const wide = useMediaQuery("(min-width: 768px)");
  const ex = (key) => t(`help.common.example.${key}`);

  const isAdmin = isAdminUser(user);
  const folderRoles = user?.folder_roles || [];
  // The subject menu's items, as the page gives them by the role in a subject: drawn with the most the reader has.
  const variants = isAdmin || folderRoles.some(canBuildVariants);
  const shares = isAdmin || folderRoles.some(canShareFolder);

  const folders = t("nav.folders");
  const bank = t("nav.question_bank");
  const openBank = t("folders_page.open_bank");
  const matrix = t("folders_page.matrix");
  const makeTest = t("folders_page.make_test");
  const share = t("folders.share.button");
  const settings = t("folders_page.settings");
  const questions = (count) => t("folders_page.questions", { count });

  // A folder as the page draws it: its icon (grey with no questions), its name and how many questions it holds;
  // `menu`: its "⋯" in the corner.
  const tile = (name, count, { mark, menu } = {}) => (
    <MiniStack key={name}>
      <MiniCard
        icon={<MiniIcon icon={FolderIcon} tone={count ? "blue" : "slate"} />}
        mark={mark}
        aside={menu && <MiniButton variant={wide ? "quiet" : "outline"} icon={EllipsisHorizontalIcon} mark={menu} />}
      >
        <MiniStack tight>
          <MiniText variant="strong" truncate>
            {name}
          </MiniText>
          <MiniText variant="muted" truncate>
            {questions(count)}
          </MiniText>
        </MiniStack>
      </MiniCard>
    </MiniStack>
  );
  // An open folder's path: "Folders › БТС-9" (its first part a link, padded like the page's).
  const path = (markFolders = false) => (
    <MiniRow>
      <MiniButton variant="quiet" mark={markFolders}>
        {folders}
      </MiniButton>
      <MiniIcon icon={ChevronRightIcon} />
      <MiniText>{ex("exam")}</MiniText>
    </MiniRow>
  );
  const examTitle = (
    <MiniStack tight>
      <MiniText variant="title" truncate>
        {ex("exam")}
      </MiniText>
      <MiniText variant="muted" truncate>
        {t("folders_page.exam_summary", { subjects: t("folders_page.subjects", { count: 2 }), questions: questions(139) })}
      </MiniText>
    </MiniStack>
  );

  return (
    <>
      <HelpIntro>
        <HelpText i18nKey="help.folders.intro" values={{ exam: ex("exam") }} />
      </HelpIntro>

      <HelpFlow pipeline="bts" current="questions" />

      <HelpSteps>
        <HelpStep
          title={t("help.folders.steps.open.title")}
          illustration={
            <MiniScreen title={folders}>
              <MiniRow className="gap-y-3">
                {tile(ex("exam"), 139, { mark: true })}
                {tile(ex("exam_2"), 64)}
                {noFolder && tile(t("folders.no_folder"), 12)}
              </MiniRow>
            </MiniScreen>
          }
        >
          {/* A paragraph of its own (the text has no pill), so the next one keeps its space. */}
          <p>{t("help.folders.steps.open.text")}</p>
          {noFolder && (
            <p>
              <HelpText i18nKey="help.folders.steps.open.text_none" values={{ none: t("folders.no_folder") }} />
            </p>
          )}
        </HelpStep>

        <HelpStep
          title={t("help.folders.steps.subject.title")}
          illustration={
            <MiniScreen title={folders}>
              {path()}
              {examTitle}
              <MiniRow className="gap-y-3">
                {tile(ex("subject"), 52, { mark: true })}
                {tile(ex("subject_2"), 87)}
              </MiniRow>
            </MiniScreen>
          }
        >
          <p>
            <HelpText i18nKey="help.folders.steps.subject.text" values={{ bank }} />
          </p>
          <p>{t("help.folders.steps.subject.text_stream")}</p>
        </HelpStep>

        <HelpStep
          title={t("help.folders.steps.path.title")}
          illustration={
            <MiniScreen title={folders}>
              {path(true)}
              {examTitle}
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.folders.steps.path.text" values={{ folders }} />
        </HelpStep>

        <HelpStep
          title={t("help.folders.steps.search.title")}
          illustration={
            <MiniScreen title={folders}>
              <MiniRow>
                <MiniField icon={MagnifyingGlassIcon} placeholder={t("folders_page.search")} value={ex("exam")} className="flex-1" mark />
              </MiniRow>
              <MiniRow className="gap-y-3">{tile(ex("exam"), 139)}</MiniRow>
            </MiniScreen>
          }
        >
          {t("help.folders.steps.search.text")}
        </HelpStep>

        <HelpStep
          title={t("help.folders.steps.menu.title")}
          illustration={
            <MiniStack>
              <MiniRow>{tile(ex("subject"), 52, { menu: "1" })}</MiniRow>
              <MiniMenu
                items={[
                  { label: openBank, icon: ClipboardDocumentListIcon },
                  { label: matrix, icon: TableCellsIcon, mark: "2" },
                  variants && { label: t("folders_page.variants"), icon: DocumentDuplicateIcon },
                  makesTests && { label: makeTest, icon: PlusIcon },
                  shares && { label: share, icon: ShareIcon },
                  isAdmin && { label: settings, icon: Cog6ToothIcon },
                  isAdmin && { label: t("folders.delete_subject"), icon: TrashIcon, danger: true },
                ]}
              />
            </MiniStack>
          }
        >
          <p>
            <HelpText i18nKey="help.folders.steps.menu.text" values={{ open: openBank, matrix }} />
          </p>
          <p>
            <HelpText i18nKey={wide ? "help.folders.steps.menu.text_hover" : "help.folders.steps.menu.text_phone"} />
          </p>
          {shares && (
            <p>
              <HelpText i18nKey="help.folders.steps.menu.text_share" values={{ share }} />
            </p>
          )}
        </HelpStep>

        {makesTests && (
          <HelpStep
            title={t("help.folders.steps.test.title")}
            illustration={
              <MiniDialog
                title={t("folders_page.test.title")}
                footer={[
                  <MiniButton key="cancel" variant="outline">
                    {t("buttons.cancel")}
                  </MiniButton>,
                  <MiniButton key="create" variant="primary" mark>
                    {t("folders_page.test.create")}
                  </MiniButton>,
                ]}
              >
                <MiniRow>
                  <MiniField label={t("folders_page.test.name")} value={`${ex("exam")} · ${ex("subject")}`} className="flex-1" />
                </MiniRow>
                <MiniCard tone="slate">{t("folders_page.test.count", { count: 24 })}</MiniCard>
              </MiniDialog>
            }
          >
            <HelpText i18nKey="help.folders.steps.test.text" values={{ make: makeTest, create: t("folders_page.test.create") }} />
          </HelpStep>
        )}

        {isAdmin && (
          <HelpStep
            title={t("help.folders.steps.admin.title")}
            illustration={
              <MiniScreen title={folders}>
                <MiniRow className="flex-nowrap">
                  <MiniField icon={MagnifyingGlassIcon} placeholder={t("folders_page.search")} className="flex-1" />
                  <MiniButton variant="primary" icon={PlusIcon} mark="1">
                    {t("folders.new_exam")}
                  </MiniButton>
                </MiniRow>
                <MiniDivider />
                <MiniRow className="justify-between gap-y-3">
                  <MiniText variant="title">{ex("exam")}</MiniText>
                  <MiniRow>
                    <MiniButton variant="primary" icon={PlusIcon} mark="2">
                      {t("folders.add_subject")}
                    </MiniButton>
                    <MiniButton variant={wide ? "quiet" : "outline"} icon={EllipsisHorizontalIcon} />
                  </MiniRow>
                </MiniRow>
              </MiniScreen>
            }
          >
            <HelpText
              i18nKey="help.folders.steps.admin.text"
              values={{ add_folder: t("folders.new_exam"), add_subject: t("folders.add_subject"), settings }}
            />
          </HelpStep>
        )}
      </HelpSteps>

      <HelpRoles
        rows={[
          { key: "admin", role: t("folders.roles.admin"), Icon: KeyIcon, can: t("help.folders.roles.admin") },
          { key: "moderator", role: t("folders.roles.moderator"), Icon: ShieldCheckIcon, can: t("help.folders.roles.moderator") },
          { key: "expert", role: t("folders.roles.expert"), Icon: CheckBadgeIcon, can: t("help.folders.roles.expert") },
          { key: "author", role: t("folders.roles.author"), Icon: PencilIcon, can: t("help.folders.roles.author") },
          { key: "viewer", role: t("folders.roles.viewer"), Icon: EyeIcon, can: t("help.folders.roles.viewer") },
        ]}
      />

      <HelpTips
        items={[
          t("help.folders.tips.grey"),
          <HelpText key="bank" i18nKey="help.folders.tips.bank" values={{ bank }} />,
          makesTests && <HelpText key="tests" i18nKey="help.folders.tips.tests" values={{ tests: t("nav.test_bank") }} />,
        ]}
      />
    </>
  );
}
